import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getReport } from '../../api';
import { PageHeader, TableSkeleton, EmptyState } from '../../components/ui';
import ExportButton from '../../components/ui/ExportButton';
import { formatDate } from '../../utils/format';
import { FileText } from 'lucide-react';

const TABS = [
  { key: 'users', label: 'Users' },
  { key: 'astrologers', label: 'Astrologers' },
  { key: 'financial', label: 'Financial' },
  { key: 'consultations', label: 'Consultations' },
] as const;

type Kind = typeof TABS[number]['key'];

export default function ReportsPage() {
  const [kind, setKind] = useState<Kind>('users');
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isLoading } = useQuery({
    queryKey: ['admin-report', kind, from, to],
    queryFn: () => getReport(kind, { from, to }).then((r) => r.data.data),
  });

  const summary = data?.summary || {};
  const summaryRows = Object.entries(summary).map(([k, v]) => ({ key: k, value: String(v) }));

  const downloadCsv = async () => {
    try {
      const r = await getReport(kind, { from, to, format: 'csv' });
      const blob = new Blob([typeof r.data === 'string' ? r.data : JSON.stringify(r.data)], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `astrovrinda-${kind}-report.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // ExportButton fallback via summary rows
    }
  };

  return (
    <div className="p-6">
      <PageHeader title="Reports" subtitle="PDF §35 — Users, Astrologers, Financial, Consultations">
        <button className="btn-secondary btn-sm" onClick={downloadCsv}>Download CSV</button>
        <ExportButton data={summaryRows} filename={`report-${kind}`} columns={[{ key: 'key', label: 'Metric' }, { key: 'value', label: 'Value' }]} />
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setKind(t.key)}
            className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${kind === t.key ? 'bg-primary-500 text-white border-primary-500' : 'border-gray-300'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="card p-4 mb-5 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
          <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={4} cols={2} />
      ) : (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-900 mb-1 capitalize">{kind} report</h3>
          <p className="text-xs text-gray-500 mb-4">
            {formatDate(from)} → {formatDate(to)} · groupBy {data?.groupBy || 'day'}
          </p>
          {summaryRows.length === 0 ? (
            <EmptyState message="No summary data" icon={<FileText size={28} />} />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {summaryRows.map((row) => (
                <div key={row.key} className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 capitalize">{row.key.replace(/([A-Z])/g, ' $1')}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{row.value}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
